import { newsConstants } from "@constants";
import { fileService, newsService } from "@services";
import { maskActions } from "@actions";
import { householdUtil, msgTypes, showModal } from "@utils";
import { message } from "antd";
import moment from "moment";
import BraftEditor from "braft-editor";

export const newsActions = {
  /**
   * 取得社區公告列表
   * @param {string} constructionUuid - 建案 uuid
   * @param {object} condition - 查詢條件
   * @returns
   */
  getAll: (constructionUuid, condition) => {
    return dispatch => {
      dispatch({
        type: newsConstants.GET_ALL_REQUEST
      });

      newsService.getAll(constructionUuid, condition).then(
        ({ items, total }) => {
          dispatch({
            type: newsConstants.GET_ALL_SUCCESS,
            constructionUuid,
            items,
            total,
            condition
          });
        },
        error => {
          dispatch({
            type: newsConstants.GET_ALL_FAILURE,
            error
          });
        }
      );
    };
  },
  /**
   * 重設社區公告列表
   * @returns
   */
  resetAll: () => ({
    type: newsConstants.RESET_ALL_SUCCESS
  }),
  /**
   * 新增社區公告資訊
   * @key CreateConstructionNewsByProperty
   * @param {string} constructionUuid - 建案 uuid
   * @param {object} info - 社區公告資訊
   * @param {function} cbSuccess - 成功後的 callback
   *
   * @returns
   */
  create: (constructionUuid, info, cbSuccess) => {
    return dispatch => {
      dispatch(maskActions.toggleMask(true));
      newsService
        .create(constructionUuid, info)
        .then(
          response => {
            cbSuccess && cbSuccess(response);
          },
          error => {
            showModal(msgTypes.ERROR, "錯誤", error);
          }
        )
        .finally(() => {
          dispatch(maskActions.toggleMask(false));
        });
    };
  },
  /**
   * 取得社區公告資訊
   * @param {number} id - 公告 id
   * @returns
   */
  getEdited: (id, cbSuccess, cbError) => {
    return dispatch => {
      dispatch(maskActions.toggleMask(true));
      newsService
        .get(id)
        .then(
          news => {
            const { published_at, content, calendar_start_at, calendar_end_at } = news;

            const transferedNews = {
              ...news,
              content: content ? BraftEditor.createEditorState(content) : null,
              published_at: published_at ? moment(published_at) : null,
              is_published: moment(published_at).isSameOrAfter(moment()),
              calendar_at_range:
                calendar_start_at && calendar_end_at
                  ? [moment(calendar_start_at), moment(calendar_end_at)]
                  : null
            };
            cbSuccess && cbSuccess(transferedNews);
          },
          error => {
            showModal(msgTypes.ERROR, "錯誤", error, cbError);
          }
        )
        .finally(() => {
          dispatch(maskActions.toggleMask(false));
        });
    };
  },
  /**
   * 更新社區公告資訊
   * @param {number} id - 社區公告 id
   * @param {object} info - 社區公告資訊
   * @param {function} cbSuccess - 成功後的 callback
   * @returns
   */
  update: (id, info, cbSuccess) => {
    return dispatch => {
      dispatch(maskActions.toggleMask(true));
      newsService
        .update(id, info)
        .then(
          response => {
            cbSuccess && cbSuccess(response);
          },
          error => {
            showModal(msgTypes.ERROR, "錯誤", error);
          }
        )
        .finally(() => {
          dispatch(maskActions.toggleMask(false));
        });
    };
  },
  /**
   * 刪除社區公告
   * @param {number} id - 社區公告 id
   * @returns
   */
  delete: id => {
    return (dispatch, getState) => {
      dispatch(maskActions.toggleMask(true));
      newsService
        .delete(id)
        .then(
          response => {
            showModal(msgTypes.SUCCESS, "通知", response, () => {
              newsActions.__getAll(dispatch, getState);
            });
          },
          error => {
            showModal(msgTypes.ERROR, "錯誤", error);
          }
        )
        .finally(() => {
          dispatch(maskActions.toggleMask(false));
        });
    };
  },
  __getAll: (dispatch, getState) => {
    const { news } = getState();
    dispatch(newsActions.getAll(news?.query?.constructionUuid, news?.query?.condition));
  },
  /**
   * 取得社區公告分類清單
   * @param {string} construcitonUuid - 建案 uuid
   * @returns
   */
  getTypeOptions: constructionUuid => {
    return dispatch => {
      dispatch({
        type: newsConstants.GET_TYPE_OPTIONS_REQUEST
      });

      newsService.getTypeOptions(constructionUuid).then(
        typeOptions => {
          dispatch({
            type: newsConstants.GET_TYPE_OPTIONS_SUCCESS,
            typeOptions
          });
        },
        error => {
          dispatch({
            type: newsConstants.GET_TYPE_OPTIONS_FAILURE,
            error
          });
        }
      );
    };
  },
  /**
   * 重設社區公告分類清單
   * @returns
   */
  resetTypeOptions: () => ({
    type: newsConstants.RESET_TYPE_OPTIONS_SUCCESS
  }),
  /**
   * 發佈社區公告
   * @param {number} id - 社區公告 id
   * @param {boolean} isPublished - 是否發佈
   * @param {boolean} isPush - 是否推播
   * @returns
   */
  publish: (id, isPublished, isPush) => {
    return (dispatch, getState) => {
      dispatch(maskActions.toggleMask(true));
      newsService
        .publish(id, {
          is_published: isPublished ? 1 : 0,
          is_push: isPush ? 1 : 0
        })
        .then(
          response => {
            showModal(msgTypes.SUCCESS, "通知", response, () => {
              newsActions.__getAll(dispatch, getState);
            });
          },
          error => {
            showModal(msgTypes.ERROR, "錯誤", error);
          }
        )
        .finally(() => {
          dispatch(maskActions.toggleMask(false));
        });
    };
  },
  /**
   * 置頂社區公告
   * @param {number} id - 社區公告 id
   * @param {boolean} is_pinned - 是否置頂
   * @returns
   */
  pin: (id, is_pinned) => {
    return (dispatch, getState) => {
      dispatch(maskActions.toggleMask(true));
      newsService
        .pin(id, {
          is_pin: is_pinned ? 1 : 0
        })
        .then(
          response => {
            showModal(msgTypes.SUCCESS, "通知", response, () => {
              newsActions.__getAll(dispatch, getState);
            });
          },
          error => {
            showModal(msgTypes.ERROR, "錯誤", error);
          }
        )
        .finally(() => {
          dispatch(maskActions.toggleMask(false));
        });
    };
  },
  /**
   * 取得社區公告讀取戶別數
   * @param {number} id - 社區公告 id
   * @returns
   */
  getReadHouseholds: id => {
    return dispatch => {
      dispatch({
        type: newsConstants.GET_HOUSEHOLD_READ_COUNT_REQUEST
      });

      newsService.getReadHouseholds(id).then(
        householdList => {
          const transferdHouseholdList = householdUtil.transferHouseholdList(householdList);
          
          dispatch({
            type: newsConstants.GET_HOUSEHOLD_READ_COUNT_SUCCESS,
            id,
            data: transferdHouseholdList
          });
        },
        error => {
          dispatch({
            type: newsConstants.GET_HOUSEHOLD_READ_COUNT_FAILURE,
            error
          });
        }
      );
    };
  },
  /**
   * 重設社區公告讀取戶別數
   * @returns
   */
  resetReadHouseholds: () => ({
    type: newsConstants.RESET_HOUSEHOLD_READ_COUNT_SUCCESS
  }),
  /**
   * 下載檔案
   * @param {string} fiileId - 檔案 id
   * @returns
   */
  download: fileId => {
    return dispatch => {
      dispatch({
        type: newsConstants.DOWNLOAD_NEWS_ATTACHMENT_REQUEST
      });

      fileService
        .download(fileId)
        .then(
          () => {
            message.success("下載成功");
          },
          error => {
            message.error(error);
          }
        )
        .finally(() => {
          dispatch({
            type: newsConstants.DOWNLOAD_NEWS_ATTACHMENT_DOWN
          });
        });
    };
  }
};
